import { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { setBreadcrumbText, setHeaderText } from "../../redux/Slice/Navigation/NavigationSlice";
import { Box, Typography, useTheme, Fab, CircularProgress, Button, LinearProgress, IconButton, TextField, FormHelperText } from "@mui/material";
// import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add"; // Material-UI icon
import styles from "./styles";
import chatStyles from "../../features/Chatbot/styles";
import { SelectItem } from "../../types/Chatbot/Chat";
import EndChatModal from "../../features/Chatbot/EndChatModal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { generateReport } from "../../services/chatbot";
import { downloadCSV } from "../../utils/csvDownload";
import UnitSelectionForm from "./UnitSelectionForm";
import paceAiLogo from "../../assets/Images/ChatAvatar.svg";

const InsurtechAI: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [tab, setTab] = useState(1); // Tab 0: Form, Tab 1: Chat
  const [project, setProject] = useState<SelectItem | null>(null);
  const [site, setSite] = useState<SelectItem | null>(null);
  const [unit, setUnit] = useState<SelectItem | null>(null);
  const [timeframe, setTimeframe] = useState<SelectItem | null>(null);
  const [isModalOpen, setModalOpen] = useState(false); // Modal open state
  const [input, setInput] = useState(""); // Input value for chat messages
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [noAlertsFound, setNoAlertsFound] = useState(false); // Loading state
  const [onStartNewConversation, setOnStartNewConversation] = useState(false);

  const handleChatClick = (selectedProject: SelectItem | null, selectedSite: SelectItem | null, selectedUnit: SelectItem | null, timeframe: SelectItem | null) => {
    // console.log("handleChatClick", { selectedProject, selectedSite, selectedUnit, timeframe });
    setProject(selectedProject);
    setSite(selectedSite);
    setUnit(selectedUnit);
    setTimeframe(timeframe);
    setIsLoading(true);
  };

  const handleBackClick = () => {
    setTab(0);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleEndChatAction = () => {
    setProject(null);
    setSite(null);
    setUnit(null);
    setTab(0); // Go back to form
    setModalOpen(false);
    setOnStartNewConversation(true);
  };

  useEffect(() => {
    dispatch(setBreadcrumbText([""]));
    if (tab === 0) {
      dispatch(setHeaderText("Insurtech Solutions"));
    }
    if (tab === 1) {
      dispatch(setHeaderText("Generate Report"));
    }
  }, [dispatch, tab]);

  // Memoizing selected values to prevent unnecessary re-fetching
  const selectedValues = useMemo(() => ({ project, site, unit, timeframe }), [project, site, unit, timeframe]);

  const {
    data: reportData,
    isLoading: isReportLoading,
    isError: isReportError,
    isSuccess: isReportSuccess,
    error: reportError,
  } = useQuery({
    queryKey: ["unitReport", selectedValues.unit?.id, selectedValues.site?.id, selectedValues.project?.id, selectedValues.timeframe?.id],
    queryFn: () => generateReport(selectedValues.unit!.id.toString(), selectedValues.site!.id.toString(), selectedValues.project!.id.toString(), Number(selectedValues.timeframe!.id)),
    enabled: !!(selectedValues.unit && selectedValues.site && selectedValues.project && selectedValues.timeframe), // Ensure all values exist before querying
    staleTime: 1000 * 60 * 5, // Cache data for 5 minutes
  });

  useEffect(() => {
    if (reportData?.reportData?.report === null && isReportSuccess && !isReportLoading) {
      setNoAlertsFound(true);
    } else {
      setNoAlertsFound(false);
    }
  }, [reportData, isReportSuccess, isReportLoading]);

  const handleSelectionChange = () => {
    console.log("handleSelectionChange");
    setNoAlertsFound(false);
    queryClient.removeQueries({ queryKey: ["unitReport"], exact: false });
    queryClient.setQueryData(["unitReport"], null);
  };

  // console.log("reportData", reportData);
  // console.log("noAlertsFound", noAlertsFound);
  // console.log("project", project);
  // console.log("site", site);
  // console.log("unit", unit);
  // console.log("timeframe", timeframe);

  return (
    <>
      <Box sx={styles.mainContainer}>
        <UnitSelectionForm
          onChatClick={handleChatClick}
          onStartNewConversation={onStartNewConversation}
          setOnStartNewConversation={setOnStartNewConversation}
          setNoAlertsFound={setNoAlertsFound}
          onSelectionChange={handleSelectionChange}
        />
        {isReportLoading && (
          <Box sx={styles.loader}>
            <CircularProgress />
          </Box>
        )}
        {reportData && reportData?.reportData?.report !== null && !noAlertsFound && project && site && unit && timeframe ? (
          <Box sx={chatStyles.answerBox}>
            <Box sx={chatStyles.avatarIcon}>
              <img src={paceAiLogo} alt="icon" width="40" height="40" />
              <Typography variant="body1" sx={chatStyles.messageUser}>
                PACE AI
              </Typography>
            </Box>
            <Box sx={styles.reportContainer}>
              <Button variant="outlined" onClick={() => downloadCSV(reportData)}>
                Download report data
              </Button>
            </Box>
          </Box>
        ) : (
          noAlertsFound && (
            <Typography variant="body1" sx={chatStyles.messageUser}>
              No alerts found
            </Typography>
          )
        )}
      </Box>
      <Box sx={styles.noHeaderInput}>
        {/* <TextField
          disabled={isLoading}
          label={"Message Insurtech AI"}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && input.trim() !== "") {
              handleSendMessage(input);
            }
          }}
          variant="outlined"
          fullWidth
          sx={styles.inputBase}
          InputProps={{
            endAdornment: (
              <IconButton onClick={() => handleSendMessage(input)} disabled={isLoading || input.trim() === ""} sx={styles.sendButton}>
                <SendIcon fontSize="small" />
              </IconButton>
            ),
          }}
        /> */}
        <FormHelperText sx={styles.helperText(theme)}>Generated content may contain inaccuracies; make sure to verify information.</FormHelperText>
      </Box>

      <Fab
        color="primary"
        sx={{
          position: "fixed",
          bottom: theme.spacing(4),
          right: theme.spacing(4),
        }}
        onClick={handleOpenModal}
      >
        <AddIcon />
      </Fab>
      {/* Modal */}
      <EndChatModal
        isOpen={isModalOpen}
        onClose={handleCloseModal} // Close modal without clearing messages
        onConfirm={handleEndChatAction} // Clears chat and closes modal
        metadata={{
          header: "Are you sure you want to Start a new conversation?",
          body: "Proceeding with this action will delete the current conversation.",
          button: "Start new conversation",
        }}
        width={450}
      />
    </>
  );
};

export default InsurtechAI;

// {tab === 0 && (
//   <Box sx={styles.container(theme)}>
//     <Typography variant="h2" sx={styles.header(theme)}>
//       SELECT A UNIT TO LOOK UP INSURANCE DETAILS
//     </Typography>
//     <UnitSelectionForm onChatClick={handleChatClick} />
//   </Box>
// )}
// {isReportLoading && (
//   <Box sx={{...styles.reportContainer, marginTop: "50px"}}>
//     <LinearProgress />{" "}
//   </Box>
// )}

// {tab === 1 &&
//   (reportData ? (
//     <Box sx={styles.reportContainer}>
//       <Button variant="outlined" onClick={() => downloadCSV(reportData)}>
//         Download report data
//       </Button>
//     </Box>
//   ) : (
//     !isReportLoading && (
//       <Box sx={styles.reportContainer}>
//         <Button>No report available</Button>
//       </Box>
//     )
//   ))}
// {tab === 1 && project && site && unit && timeframe && !isReportLoading && (
//   <InsurtechChat reportData={reportData} project={project} site={site} unit={unit} timeframe={timeframe} onBackClick={handleBackClick} />
// )}

// {/* Floating Action Button */}
// {tab === 1 && (
//   <Fab
//     color="primary"
//     sx={{
//       position: "fixed",
//       bottom: theme.spacing(4),
//       right: theme.spacing(4),
//     }}
//     onClick={handleOpenModal}
//   >
//     <AddIcon />
//   </Fab>
// )}
